import { markAdminValidationMessages } from './admin-i18n/validation-source'
import * as allValidators from 'vuelidate/lib/validators'
import * as http from './http'
import { decodePaymentMethod } from './payment-methods'

import i18next from 'i18next'
import { decode } from 'jsonwebtoken'
import { flatten } from 'lodash'
import moment from 'moment'
import uuid from 'uuid/v4'
import { date as dateValidator, maxDate, minDate } from './validators'
import { setRevolutCheckoutActive } from './revolut-checkout-state'
import { EventBus } from './plugins/event-bus'

export const getValidationErrors = messages => {
  return field => {
    if (!field || !field.$dirty) return []

    let keys = Object.keys(field)
    const errorKeys = keys.filter(x => !field[x] && x in messages)

    return errorKeys.map(ek => messages[ek])
  }
}

export const submitGuard = async (instance, submitFunction, handleError) => {
  instance.$v.$touch()
  if (instance.$v.$invalid || instance.submitInProgress) {
    if (handleError) return handleError(instance)

    return console.warn('Form not valid')
  }
  instance.submitInProgress = true
  try {
    await submitFunction()
  } finally {
    instance.submitInProgress = false
  }
}

export const defaultMessages = {
  required: 'This field is required',
  date: 'This field must be date',
  between: 'This field is out of range',
  minLength: 'This field should contain more values',
  maxLength: 'This field should contain less values',
  minDate: 'This field should be after',
  maxDate: 'This field should be before',
  email: 'This field must be an email',
  httpUrl: 'This field must be http or https url',
  minValue: 'Provided value is too small',
  maxValue: 'Provided value is too big',
  sameAs: 'This field must be the same as some other field',
  notIn: 'Provided value is already taken',
  numeric: 'This field must be a number',
  url: 'This field must a valid url',
  uniqueTicketCategoryPriority: 'Category priority is already in use',
  sameAsEmail: 'Email addresses do not match. Please ensure both email fields are identical',
  minValueDynamic: 'Value cannot be less than minimum required',
  minValueDynamicAdditionalCapacity: 'Value cannot be less than minimum required',
}

// Creates a validator that ensures value >= parent[minField]
export const createMinValueValidator = minField => {
  return (value, parentModel) => {
    if (!parentModel) return true
    const minRaw = parentModel[minField]
    const min = typeof minRaw === 'number' ? minRaw : parseFloat(minRaw)
    if (min === undefined || min === null || Number.isNaN(min)) return true
    if (value === undefined || value === null || value === '') return true
    const v = typeof value === 'number' ? value : parseFloat(value)
    if (Number.isNaN(v)) return true

    return v >= min
  }
}

export const POSTAL_CODE_KEYS = new Set(['postalCode', 'Postal code'])

export const isPostalCodeField = key => POSTAL_CODE_KEYS.has(key)

export const normalizePostalCodeFieldDefinition = (key, field) => {
  if (!isPostalCodeField(key)) return field

  const validations = { ...(field.validations || {}) }
  delete validations.numeric

  return {
    ...field,
    fieldID: 'text',
    type: 'text',
    validations,
  }
}

export const normalizePostalCodeValue = values => {
  const normalized = { ...values }

  for (const key of POSTAL_CODE_KEYS) {
    if (normalized[key] != null) {
      normalized[key] = String(normalized[key])
    }
  }

  return normalized
}

export const tvornicaKultureCustomOrganizerMsg =
  'UPOZORENJE POSJETITELJIMA!\n' +
  'PUŠENJE u Velikom pogonu Tvornice kulture je NAJSTROŽE ZABRANJENO!\n' +
  'Posjetitelji koji neće poštovati zabranu pušenja biti će udaljeni iz dvorane!'

/**
 * Resolves the VAT percentage for an event as a number.
 * Prefers the event-level VAT, falls back to the owning organization's VAT, then 0.
 */
export const getEventVAT = event => {
  let raw = null
  if (event && event.VATPercentage !== null && event.VATPercentage !== undefined) {
    raw = event.VATPercentage
  } else if (event && event.organization && event.organization.VATPercentage != null) {
    raw = event.organization.VATPercentage
  }
  const parsed = Number(raw)

  return Number.isFinite(parsed) ? parsed : 0
}

/**
 * Selectable event tax classes, with the copy explaining when each one applies.
 * Mirrors ALLOWED_EVENT_VAT_PERCENTAGES on the backend (minus 0, which is the
 * "no tax" state expressed by the toggles rather than by a radio option).
 */
export const EVENT_VAT_RATES = [
  {
    value: 5,
    description: '— for concerts, festivals, movie projections, and sports and culture events',
  },
  {
    value: 13,
    description: '— for catering/food services and accommodation related to the event',
  },
  { value: 25, description: '— for other events' },
]

export const EVENT_VAT_NOTE =
  'Note: Tax rates depend on the specific nature of your event and may vary. Please consult your ' +
  'accountant or the Tax Administration to confirm the correct rate before publishing your event.'

export const hexToRGB = hex => {
  const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i
  const longHex = hex.replace(shorthandRegex, (m, r, g, b) => {
    return r + r + g + g + b + b
  })

  const colors = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(longHex)

  return colors
    ? {
      r: parseInt(colors[1], 16),
      g: parseInt(colors[2], 16),
      b: parseInt(colors[3], 16),
    }
    : null
}

export const rgbaTorgb = RGBA => {
  const RGB = {}
  const bg = { r: 255, g: 255, b: 255 }
  const alpha = 1 - RGBA.a
  RGB.r = Math.round((RGBA.a * (RGBA.r / 255) + alpha * (bg.r / 255)) * 255)
  RGB.g = Math.round((RGBA.a * (RGBA.g / 255) + alpha * (bg.g / 255)) * 255)
  RGB.b = Math.round((RGBA.a * (RGBA.b / 255) + alpha * (bg.b / 255)) * 255)

  return RGB
}

export const calculateLuminance = (r, g, b) => {
  // for more info about the luminance constants
  // https://www.w3.org/TR/WCAG20-TECHS/G17.html#G17-procedure
  const a = [r, g, b].map(function (v) {
    v /= 255

    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4)
  })

  return a[0] * 0.2126 + a[1] * 0.7152 + a[2] * 0.0722
}

export const calculateContrast = (rgb1, rgb2) => {
  const lum1 = calculateLuminance(rgb1[0], rgb1[1], rgb1[2])
  const lum2 = calculateLuminance(rgb2[0], rgb2[1], rgb2[2])
  const brightest = Math.max(lum1, lum2)
  const darkest = Math.min(lum1, lum2)

  return (brightest + 0.05) / (darkest + 0.05)
}

export const WCGAcheck = (chosenColor, colors, opacity) => {
  const { r, g, b } = chosenColor
  const defaultOpacity = 0.05
  const RGBA = { ...chosenColor }
  RGBA.a = opacity || defaultOpacity
  const RGB = rgbaTorgb(RGBA)
  if (calculateContrast([r, g, b], [RGB.r, RGB.g, RGB.b]) < 4.5) {
    chosenColor = { ...colors.primary }

    return chosenColor
  }

  return chosenColor
}

export const validateWorkshops = (selectedWorkshops, workshops) => {
  return flatten(
    selectedWorkshops.map(selectedWorkshop => {
      const from = moment(selectedWorkshop.from)
      const to = moment(selectedWorkshop.to)

      return workshops.filter(w => {
        const wFrom = moment(w.from)
        const wTo = moment(w.to)

        return from.isBefore(wTo) && to.isAfter(wFrom)
      })
    }),
  )
}

export const findCountry = (worldCountries, parsedCountry) => {
  return worldCountries.find(({ official, common, ioc, iso2, iso3, fifa, other }) => {
    const officialMatch = official && official.toLowerCase().trim() === parsedCountry
    const commonMatch = common && common.toLowerCase().trim() === parsedCountry
    const iocMatch = ioc && ioc.toLowerCase().trim() === parsedCountry
    const iso2Match = iso2 && iso2.toLowerCase().trim() === parsedCountry
    const iso3Match = iso3 && iso3.toLowerCase().trim() === parsedCountry
    const fifaMatch = fifa && fifa.toLowerCase().trim() === parsedCountry
    const otherMatch = other && other.map(o => o.toLowerCase().trim()).includes(parsedCountry)

    return (
      officialMatch || commonMatch || iocMatch || iso2Match || iso3Match || fifaMatch || otherMatch
    )
  })
}

export const toCSS = object => {
  const styles = {}
  if (object.size) {
    styles['font-size'] = `${object.size}px`
  }
  if (object.font) {
    styles['font-family'] = object.font
  }
  if (object.color) {
    styles['color'] = object.color
  }
  if (object.textAlign) {
    styles['text-align'] = object.textAlign
  }
  if (object.backgroundColor) {
    styles['background-color'] = object.backgroundColor
  }
  if (object.bold) {
    styles['font-weight'] = 'bold'
  }
  if (object.italic) {
    styles['font-style'] = 'italic'
  }
  if (object.underline) {
    styles['text-decoration'] = 'underline'
  }
  if (object.uppercase) {
    styles['text-transform'] = 'uppercase'
  }
  if (object.backgroundSize) {
    styles['background-size'] = object.backgroundSize
  }
  if (object.backgroundImage) {
    styles['background-image'] = `url(${object.backgroundImage})`
  }
  if (object.borderRadius) {
    styles['border-radius'] = `${object.borderRadius}px`
  }
  if (object.z) {
    styles['z-index'] = object.z
  }
  if (object.x) {
    styles['left'] = `${object.x}px`
  }
  if (object.y) {
    styles['top'] = `${object.y}px`
  }
  if (object.w) {
    styles['width'] = `${object.w}px`
  }
  if (object.h) {
    styles['height'] = `${object.h}px`
  }
  if (object.rotate) {
    styles['transform'] = `rotate(${object.rotate}deg)`
  }
  if (object.position) {
    styles['position'] = object.position
  }
  if (object.clip) {
    styles['clip'] = object.clip
  }
  if (object.top) {
    styles['top'] = `${object.top}px`
  }

  return styles
}

export const parseDate = (date, format = 'DD.MM.YYYY') => {
  return moment(date).format(format)
}

export const dataURIToFile = (uri, name, type) => {
  const binStr = atob(uri.split(',')[1])
  let arr = new Uint8Array(binStr.length)
  for (let i = 0; i < binStr.length; i++) {
    arr[i] = binStr.charCodeAt(i)
  }
  // File constructor does not exist in Edge

  return new File([arr], name, { type })
}

export const serverURL = process.env.VUE_APP_SERVER_URL
export const frontendURL = process.env.VUE_APP_FRONTEND_URL
export const customerURL = process.env.VUE_APP_CUSTOMER_URL
export const landingURL = process.env.VUE_APP_LANDING_URL
export const zkmURL = process.env.VUE_APP_ZKM_URL

export const dataURLToImage = url => {
  return new Promise(resolved => {
    const image = new Image()
    image.onload = function () {
      resolved(this)
    }
    image.crossOrigin = 'Anonymous'
    image.src = url
  })
}

export const resizeWidthAndHeight = (width, height, MAX_WIDTH = 300, MAX_HEIGHT = 300) => {
  if (width > height) {
    if (width > MAX_WIDTH) {
      return {
        height: (height * MAX_WIDTH) / width,
        width: MAX_WIDTH,
      }
    }
  } else {
    if (height > MAX_HEIGHT) {
      return {
        width: (width * MAX_HEIGHT) / height,
        height: MAX_HEIGHT,
      }
    }
  }

  return { width, height }
}

export const resizeImage = async (image, options) => {
  const { width, height } = resizeWidthAndHeight(
    image.width,
    image.height,
    options.width,
    options.height,
  )
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const context = canvas.getContext('2d')
  context.drawImage(image, 0, 0, width, height)
  const toFile = options && options.to === 'file'

  return new Promise(resolve => {
    if (toFile) {
      canvas.toBlob(resolve, options.mime || 'image/jpeg', 1.0)
    } else {
      const dataURL = canvas.toDataURL(options.mime || 'image/jpeg')

      return resolve(dataURLToImage(dataURL))
    }
  })
}

export const fileToImage = async file => {
  if (!(file instanceof Blob)) {
    return Promise.reject({ error: 'Invalid file format' })
  }

  return new Promise(resolve => {
    const fr = new FileReader()
    fr.onload = () => {
      const img = new Image()
      img.onload = () => resolve(img)
      img.src = fr.result.toString()
    }
    fr.readAsDataURL(file)
  })
}

export const fileToDataUrl = async file => {
  if (!(file instanceof Blob)) {
    return Promise.reject({ error: 'Invalid file format' })
  }

  return new Promise(resolve => {
    const fr = new FileReader()
    fr.onload = () => {
      resolve(fr.result)
    }
    fr.readAsDataURL(file)
  })
}

export const urlToDataURL = async url => {
  const image = await dataURLToImage(url)

  const canvas = document.createElement('canvas')
  canvas.width = image.width
  canvas.height = image.height
  const context = canvas.getContext('2d')
  context.drawImage(image, 0, 0, image.width, image.height)

  return canvas.toDataURL()
}

export const uploadImageFromDataUrl = async (dataURL, path) => {
  const image = await dataURLToImage(dataURL)
  const file = await resizeImage(image, { to: 'file' })
  const { upload: uploadURL, download: downloadURL } = await http.getSignedURL(path)
  await http.uploadImage(uploadURL, file)

  return downloadURL
}

export const uploadImage = async (file, path) => {
  const { upload: uploadURL, download: downloadURL } = await http.getSignedURL(path)
  await http.uploadImage(uploadURL, file)

  return downloadURL
}

export function getUserFromToken(token) {
  return decode(token)
}

export function strToBool(str) {
  return str ? str.toLowerCase().trim() === 'true' : undefined
}

export const isSafari = () => {
  return (
    navigator.vendor &&
    navigator.vendor.indexOf('Apple') > -1 &&
    navigator.userAgent &&
    navigator.userAgent.indexOf('CriOS') == -1 &&
    navigator.userAgent.indexOf('FxiOS') == -1
  )
}

export const getMimeFromDataURL = dataURL => {
  return dataURL.substring('data:image/'.length, dataURL.indexOf(';base64'))
}

export const isDataURL = string => {
  return string.startsWith('data:image/')
}

export const getUploadOptions = async (data, path, options = {}) => {
  if (options.resize === false) {
    return { file: data, path: `${path}/${uuid()}` }
  }
  const mime = getMimeFromDataURL(data)
  const image = await dataURLToImage(data)
  const { width, height } = options
  const file = await resizeImage(image, { to: 'file', mime, width, height })

  return { file, path: `${path}/${uuid()}` }
}

export async function validateEventRoutingRules(rules, event, userType) {
  if (!rules || rules.length === 0) {
    return true
  }
  for (const rule of rules) {
    if (!(await rule(event, userType))) return false
  }

  return true
}

export async function validateRoutingRules(rules) {
  const user = await http.getUser()
  if (!user) return false

  for (const rule of rules) {
    if (!(await rule(user))) return false
  }

  return true
}

export const formatDate = (date, format) => moment(date).format(format || 'LL, H:mm')

export const isSpecialTableLocationEvent = eventId => [7345, 9022].includes(Number(eventId))

export const formatTicketLocationDisplay = (
  location,
  { eventId, language = i18next.language } = {},
) => {
  if (!location) return ''

  const section = location.section || ''
  let row = location.row || ''
  const seatLabel = location.isTable ? 'Table' : 'Seat'
  const seatsLocation =
    Array.isArray(location.seats) && location.seats.length
      ? `${seatLabel}-${location.seats.join(',')}`
      : ''

  if (isSpecialTableLocationEvent(eventId)) {
    row = row.replace('Row', language === 'hr' ? 'Stol' : 'Table')

    return [section, row].filter(Boolean).join(' / ')
  }

  return [section, row, seatsLocation].filter(Boolean).join(' / ')
}

export const parseTicketLocationDisplayFields = (
  ticketLocation,
  { eventId, language = i18next.language, isTable = false } = {},
) => {
  if (!ticketLocation) return null

  const ticketLocationDto = {}
  const specialTableEvent = isSpecialTableLocationEvent(eventId)

  ticketLocation.split('_').forEach(item => {
    let [key, ...value] = item.split('-')

    if (key === 'Seat' && specialTableEvent) {
      return
    }

    if (language !== 'en') {
      if (key === 'Seat') key = isTable ? 'Stol' : 'Sjedalo'
      if (key === 'Table') key = 'Stol'
      if (key === 'Row') key = specialTableEvent ? 'Stol' : 'Red'
      if (key === 'Section') key = 'Sektor'
    } else {
      if (key === 'Seat') key = isTable ? 'Table' : key
      if (key === 'Row' && specialTableEvent) key = 'Table'
    }

    ticketLocationDto[key] = value.join('-')
  })

  return ticketLocationDto
}

export const getTimeUntilExpiresAsString = expirationDate => {
  const now = moment()
  const expires = moment(expirationDate)
  const duration = moment.duration(expires.diff(now))

  const years = duration.years()
  const months = duration.months()
  const days = duration.days()
  const hours = duration.hours()
  const minutes = duration.minutes()

  const parts = []
  if (years > 0) parts.push(`${years}y`)
  if (months > 0) parts.push(`${months}m`)
  if (days > 0) parts.push(`${days}d`)
  if (hours > 0) parts.push(`${hours}h`)
  if (minutes > 0) parts.push(`${minutes}min`)

  return parts.length > 0 ? parts.join(' ') : 'Expired'
}

export const createAttendeeSchema = (definition, values = {}) => {
  const validationSchema = {}
  const attendeeFields = []
  const attendeeFieldValidators = {
    date: dateValidator,
  }
  for (const [key, field] of Object.entries(definition)) {
    const normalizedField = normalizePostalCodeFieldDefinition(key, field)
    const lng = i18next.resolvedLanguage || i18next.language
    const ns = 'default'
    const validators = {}
    const errorMessages = {
      required: i18next.t('required'),
      date: i18next.t('date'),
      between: i18next.t('between'),
      minLength: i18next.t('minLength'),
      maxLength: i18next.t('maxLength'),
      minDate: i18next.t('minDate'),
      maxDate: i18next.t('maxDate'),
      email: i18next.t('EmailError'),
      httpUrl: i18next.t('httpUrl'),
      minValue: i18next.t('minValue'),
      maxValue: i18next.t('maxValue'),
      sameAs: i18next.t('sameAs'),
      notIn: i18next.t('notIn'),
      numeric: i18next.t('numeric'),
      url: i18next.t('url'),
    }

    const formatBoundaryDate = value => {
      if (!value) return ''

      return moment(value).format(lng === 'hr' ? 'DD.MM.YYYY.' : 'YYYY-MM-DD')
    }

    if (normalizedField.validations && normalizedField.validations.minDate) {
      errorMessages.minDate =
        lng === 'hr'
          ? `Datum mora biti na ili nakon ${formatBoundaryDate(
            normalizedField.validations.minDate,
          )}`
          : `Date must be on or after ${formatBoundaryDate(normalizedField.validations.minDate)}`
    }

    if (normalizedField.validations && normalizedField.validations.maxDate) {
      errorMessages.maxDate =
        lng === 'hr'
          ? `Datum mora biti na ili prije ${formatBoundaryDate(
            normalizedField.validations.maxDate,
          )}`
          : `Date must be on or before ${formatBoundaryDate(normalizedField.validations.maxDate)}`
    }

    Object.entries(normalizedField.validations || []).forEach(([key, value]) => {
      if (value) {
        // A hidden field is never rendered, so a required validator on it
        // would make the form permanently unsubmittable.
        if (key === 'required' && normalizedField.isPrivate) {
          return
        }
        const isRequiredSwitchField =
          key === 'required' && normalizedField.component === 'switch-field'
        if (isRequiredSwitchField) {
          validators['sameAs'] = allValidators['sameAs'](() => true)
          errorMessages['sameAs'] = 'This field is required'
        } else if (key === 'minDate') {
          validators[key] = minDate(() => value)
        } else if (key === 'maxDate') {
          validators[key] = maxDate(() => value)
        } else if (attendeeFieldValidators[key]) {
          validators[key] = attendeeFieldValidators[key]
        } else if (allValidators[key]) {
          validators[key] = allValidators[key]
        }
      }
    })
    let label = key

    let translatedKey = i18next.getResource(lng, ns, key)

    if (translatedKey) {
      label = translatedKey
    } else if (key === 'First name') label = i18next.t('firstName')
    else if (key === 'Last name') label = i18next.t('lastName')
    else if (key === 'postalCode' || key === 'Postal code') label = i18next.t('postalCode')
    else if (key === 'City') label = i18next.t('cityOptional')
    else if (key === 'Country') label = i18next.t('countryOptional')
    else if (['Email', 'Address'].includes(key)) label = i18next.t(key.toLowerCase())

    markAdminValidationMessages(errorMessages, {
      fieldKey: key,
      minDate: normalizedField.validations && normalizedField.validations.minDate,
      maxDate: normalizedField.validations && normalizedField.validations.maxDate,
      requiredSwitch: normalizedField.component === 'switch-field' && normalizedField.validations && normalizedField.validations.required,
    })
    attendeeFields.push({
      key,
      value: values[key],
      min:
        normalizedField.validations && normalizedField.validations.minDate
          ? normalizedField.validations.minDate
          : null,
      max:
        normalizedField.validations && normalizedField.validations.maxDate
          ? normalizedField.validations.maxDate
          : null,
      ...normalizedField,
      label,
      errorMessages,
    })
    validationSchema[key] = validators
  }

  return {
    validationSchema,
    attendeeFields: attendeeFields.sort((a, b) => a.order - b.order),
  }
}

export const objectToArray = (object, field) => {
  return Object.entries(object).map(([key, value]) => {
    return field
      ? {
        [field]: key,
        ...value,
      }
      : value
  })
}

export const findMaxReducer = mapper => (acc, item) => {
  if (mapper) return mapper(item) > acc ? mapper(item) : acc

  return item > acc ? item : acc
}

export const getArray = object => {
  if (Array.isArray(object)) return object

  return [object]
}
export const isDateBetween = val => {
  const first = Math.floor(moment(new Date()).diff(new Date(val.from), 'days', true)) >= 0
  const last = Math.ceil(moment(new Date()).diff(new Date(val.to), 'days', true)) <= 0

  return first && last
}

export const getLecturerNames = lecturers => {
  return (lecturers || [])
    .map(({ fields }) => `${fields['First name']} ${fields['Last name']}`)
    .join(', ')
}

export const setVisibleFields = (visibleFields, eventId) => {
  const allFields = JSON.parse(localStorage.getItem('visibleFields')) || {}

  localStorage.setItem(
    'visibleFields',
    JSON.stringify({ ...allFields, [eventId]: visibleFields.slice(0, 10) }),
  )
}

export const getVisibleFields = eventId => {
  const allFields = JSON.parse(localStorage.getItem('visibleFields')) || {}
  const visibleFields = allFields[eventId]

  return visibleFields || []
}

export const encodeExportVisibleFields = visibleFields => {
  return encodeURIComponent(JSON.stringify(visibleFields))
}

export async function initializeRegisterForm(order) {
  let form = { attendee: { fields: {} } }
  let shoppingCart = order.purchases.map(p => ({
    ...p.orderItem,
    ...(p.bundleOfferId != null && { bundleOfferId: p.bundleOfferId, price: 0 }),
  }))

  return [form, shoppingCart]
}

/** The server owns the enabled list. Repeated clicks share one customer choice. */
let providerChoicePromise = null
export function choosePaymentProvider() {
  if (providerChoicePromise) return providerChoicePromise
  providerChoicePromise = (async () => {
    const { enabledProviders } = await http.getPaymentCheckoutCapabilities()
    if (!Array.isArray(enabledProviders) || !enabledProviders.length) {
      throw new Error('No payment provider is available')
    }
    if (enabledProviders.length === 1) return enabledProviders[0]

    return new Promise((resolve, reject) => {
      EventBus.$emit('payment-provider:choose', { enabledProviders, resolve, reject })
    })
  })()
  providerChoicePromise.then(
    () => { providerChoicePromise = null },
    () => { providerChoicePromise = null },
  )
  return providerChoicePromise
}

export async function createPurchaseCheckout(
  form,
  totalPrice,
  order,
  lang = 'HR',
  shoppingCart,
  eventId = '',
  options = {},
) {
  const paymentOrder = order.createdOrder || order
  const checkout = await http.createPaymentCheckout({
    orderId: paymentOrder.id || paymentOrder.orderId || paymentOrder.order_id,
    orderUuid: paymentOrder.uuid || paymentOrder.orderUuid || paymentOrder.order_uuid,
    ...decodePaymentMethod(form.paymentMethod, process.env.VUE_APP_PAYMENT_PROVIDER || 'WSPAY'),
    language: lang,
    flow: options.redirectType || (eventId ? 'event' : 'store'),
  })

  return checkout
}

export async function createCustomPaymentCheckout(
  form,
  totalPrice,
  order,
  shoppingCart,
  eventId = '',
) {
  const paymentOrder = order.createdOrder || order
  const checkout = await http.createPaymentCheckout({
    orderId: paymentOrder.id || paymentOrder.orderId || paymentOrder.order_id,
    orderUuid: paymentOrder.uuid || paymentOrder.orderUuid || paymentOrder.order_uuid,
    ...decodePaymentMethod(form.paymentMethod, process.env.VUE_APP_PAYMENT_PROVIDER || 'WSPAY'),
    language: i18next.language || 'hr',
    flow: 'custom',
  })

  return checkout
}

export async function createTheaterSubscriptionPaymentCheckout(
  form,
  totalPrice,
  order,
  lang = 'HR',
  shoppingCart,
  eventId = '',
) {
  const paymentOrder = order.createdOrder || order
  const checkout = await http.createPaymentCheckout({
    orderId: paymentOrder.id || paymentOrder.orderId || paymentOrder.order_id,
    orderUuid: paymentOrder.uuid || paymentOrder.orderUuid || paymentOrder.order_uuid,
    ...decodePaymentMethod(form.paymentMethod, process.env.VUE_APP_PAYMENT_PROVIDER || 'WSPAY'),
    language: lang,
    flow: 'theater-subscription',
  })

  return checkout
}

/**
 * Starts the transport selected by the backend. Card-form providers keep their
 * existing POST redirect; Revolut Pay mounts the SDK button.
 */
export function startPaymentCheckout(checkout) {
  if (checkout && checkout.checkoutType === 'REVOLUT_PAY' && checkout.provider === 'REVOLUT') {
    return new Promise((resolve, reject) => {
      EventBus.$emit('revolut-pay:open', { checkout, resolve, reject })
    })
  }
  if (checkout && checkout.checkoutType === 'REVOLUT_HOSTED' && checkout.provider === 'REVOLUT') {
    const checkoutUrl = new URL(checkout.checkoutUrl)
    if (checkoutUrl.protocol !== 'https:' || !/\.revolut\.com$/i.test(checkoutUrl.hostname)) {
      throw new Error('Revolut hosted checkout must use a secure Revolut URL')
    }
    window.sessionStorage.setItem('revolut-pay-checkout', JSON.stringify(checkout))
    setRevolutCheckoutActive(true)
    window.location.assign(checkoutUrl.toString())

    return Promise.resolve()
  }

  const form = createProviderPaymentForm(checkout)
  document.body.appendChild(form)
  form.submit()

  return Promise.resolve()
}

// Compatibility exports for feature branches that have not yet migrated. They
// now return a provider-neutral checkout response rather than assuming a form.
export const createPurchaseSubmitFormWSPay = createPurchaseCheckout
export const createCustomPurchaseSubmitFormWSPay = createCustomPaymentCheckout
export const createTheaterSubscriptionPurchaseSubmitFormWSPay = createTheaterSubscriptionPaymentCheckout

export function createProviderPaymentForm(checkout) {
  if (!checkout || checkout.method !== 'POST' || !checkout.action || !checkout.fields) {
    throw new Error('Payment checkout response is invalid')
  }

  const action = new URL(checkout.action)
  if (action.protocol !== 'https:' && action.hostname !== 'localhost') {
    throw new Error('Payment checkout must use HTTPS')
  }

  const submitForm = document.createElement('form')
  submitForm.method = 'POST'
  submitForm.action = checkout.action
  Object.entries(checkout.fields).forEach(([key, value]) => {
    const customInput = document.createElement('input')
    customInput.type = 'hidden'
    customInput.name = key
    customInput.value = value == null ? '' : String(value)
    submitForm.appendChild(customInput)
  })
  document.body.appendChild(submitForm)

  return submitForm
}

export const exchangeRate = 7.5345

export const heatmapColorsArray = ['#E6F1F8', '#CEE4F2', '#56A4EF', '#0277BD', '#004C8C']

export const tisakTicketSeller = 'tisak'

export const croatianMonths = [
  'Siječanj',
  'Veljača',
  'Ožujak',
  'Travanj',
  'Svibanj',
  'Lipanj',
  'Srpanj',
  'Kolovoz',
  'Rujan',
  'Listopad',
  'Studeni',
  'Prosinac',
]

export const convertUtcToCroatianLocalTime = (dateTime, format) => {
  return moment.utc(dateTime, format).tz('Europe/Zagreb').format(format)
}

export const chartBackgroundColors = [
  '#0277BD',
  '#CEE4F2',
  '#6B02BD',
  '#B902BD',
  '#BD0202',
  '#BD5C02',
  '#988A14',
  '#0E7E1A',
  '#0F8890',
]

export const isHNBOrganization = id => {
  return [271, 129, 165].includes(id)
}

export const showPromoCodeInputInRegisterContainer = (eventId, organizationId) => {
  // add event id in array to remove promo code input
  const eventIdsWithoutPromoCodeInput = [1744, 1745, 2787, 3377, 2247, 2444, 4531, 8265]
  const shouldEventHavePromoCodeInput = !eventIdsWithoutPromoCodeInput.includes(eventId)

  return shouldEventHavePromoCodeInput && !isHNBOrganization(organizationId)
}

const inAppBrowsers = [
  'FBAN',
  'FBAV',
  'Twitter',
  'Instagram',
  'TikTok',
  'FBAN/Messenger',
  'FB_IAB/MESSENGER',
]

export const isInAppBrowser = userAgent => {
  return inAppBrowsers.some(item => userAgent.includes(item))
}

export const getIOSVersion = userAgent => {
  const match = userAgent.match(/OS (\d+)_(\d+)_?(\d+)?/)
  if (match && match.length > 1) {
    return parseInt(match[1], 10)
  }

  return null
}
